import { useEffect, useState } from 'react';
import { readError, request } from '../../app/api';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import {
  createAnalyticsFilterQuery,
  type AnalyticsFilterScope,
} from './AnalyticsFilterScope';
import { ANALYTICS_PAGE_SIZE } from './analytics-api';
import { EventOccurrenceDetails, EventOccurrenceSummary } from './EventOccurrencePresentation';
import { PaginationControls } from './PaginationControls';
import type { AnalyticsEventRecord, EventCatalog, EventPage } from './types';

export function EventExplorerView(props: {
  environmentId: string;
  filters: AnalyticsFilterScope;
  onChangeFilters: (filters: AnalyticsFilterScope) => void;
}): React.JSX.Element {
  const [catalog, setCatalog] = useState<EventCatalog | null>(null);
  const [result, setResult] = useState<EventPage | null>(null);
  const [page, setPage] = useState(1);
  const [categoryKey, setCategoryKey] = useState('');
  const [eventKey, setEventKey] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setCatalog(null);
    request<EventCatalog>(`/admin/v1/reports/catalog?environmentId=${encodeURIComponent(props.environmentId)}`)
      .then((response) => { if (active) setCatalog(response); })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); });
    return () => { active = false; };
  }, [props.environmentId]);

  useEffect(() => {
    setPage(1);
  }, [props.environmentId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    loadPage(props.environmentId, props.filters, categoryKey, eventKey, page)
      .then((response) => {
        if (active) {
          setResult(response);
          if (response.pagination.page !== page) setPage(response.pagination.page);
          setSelected(response.events[0]?.eventId ?? null);
        }
      })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [props.environmentId, props.filters, categoryKey, eventKey, page]);

  const events = result?.events ?? [];
  const availableEvents = catalog?.events.filter((event) => categoryKey === '' || event.categoryKey === categoryKey) ?? [];
  const selectedEvent = events.find((event) => event.eventId === selected);
  return (
    <section className="analytics-page">
      <AnalyticsFilterBar environmentId={props.environmentId} scope={props.filters} onChange={changeFilters} />
      <div className="filter-bar surface">
        <label>Category<select value={categoryKey} onChange={changeCategory}><option value="">All categories</option>{catalog?.categories.map(renderCategory)}</select></label>
        <label>Event<select value={eventKey} onChange={changeEvent}><option value="">All events</option>{availableEvents.map(renderCatalogEvent)}</select></label>
        <span>Event-specific filters · newest first</span>
      </div>
      {error !== '' && <div className="error-banner">{error}</div>}
      <div className="explorer-layout">
        <article className="surface explorer-list">
          <div className="surface-heading"><div><span className="eyebrow">Facts</span><h2>Event stream</h2></div><strong>{formatNumber(result?.pagination.totalItems ?? 0)}</strong></div>
          {events.map((event) => <EventButton key={event.eventId} event={event} active={event.eventId === selected} onSelect={setSelected} />)}
          {events.length === 0 && !loading && <Empty text="No matching events in this time range." />}
          {result !== null && <PaginationControls pagination={result.pagination} disabled={loading} onPageChange={setPage} />}
        </article>
        <EventInspectorPanel key={selectedEvent?.eventId ?? 'empty'} event={selectedEvent} />
      </div>
    </section>
  );

  function changeCategory(event: React.ChangeEvent<HTMLSelectElement>): void {
    setPage(1);
    setCategoryKey(event.target.value);
    setEventKey('');
  }

  function changeEvent(event: React.ChangeEvent<HTMLSelectElement>): void {
    setPage(1);
    setEventKey(event.target.value);
  }

  function changeFilters(filters: AnalyticsFilterScope): void {
    setPage(1);
    props.onChangeFilters(filters);
  }
}

function EventButton(props: { event: AnalyticsEventRecord; active: boolean; onSelect: (id: string) => void }): React.JSX.Element {
  return <button className={props.active ? 'explorer-event active' : 'explorer-event'} type="button" aria-pressed={props.active} aria-controls="event-explorer-inspector" onClick={() => props.onSelect(props.event.eventId)}><EventOccurrenceSummary event={props.event} variant="explorer" /></button>;
}

function EventInspectorPanel(props: { event: AnalyticsEventRecord | undefined }): React.JSX.Element {
  const event = props.event;
  if (event === undefined) {
    return <article id="event-explorer-inspector" className="surface event-inspector"><Empty text="Choose an event to inspect its schema-resolved values." /></article>;
  }
  return (
    <article id="event-explorer-inspector" className="surface event-inspector">
      <div className="event-inspector-scroll" tabIndex={0} aria-label="Selected event details">
        <EventOccurrenceDetails event={event} variant="panel" />
      </div>
    </article>
  );
}

async function loadPage(
  environmentId: string,
  filters: AnalyticsFilterScope,
  categoryKey: string,
  eventKey: string,
  page: number,
): Promise<EventPage> {
  const query = createAnalyticsFilterQuery(environmentId, filters);
  query.set('page', String(page));
  query.set('pageSize', String(ANALYTICS_PAGE_SIZE));
  if (categoryKey !== '') query.set('categoryKey', categoryKey);
  if (eventKey !== '') query.set('eventKey', eventKey);
  return request<EventPage>(`/admin/v1/reports/records?${query.toString()}`);
}

function renderCategory(category: EventCatalog['categories'][number]): React.JSX.Element {
  return <option key={category.key} value={category.key}>{category.displayName}</option>;
}

function renderCatalogEvent(event: EventCatalog['events'][number]): React.JSX.Element {
  return <option key={event.key} value={event.key}>{event.displayName}</option>;
}

function Empty(props: { text: string }): React.JSX.Element { return <div className="empty">{props.text}</div>; }

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
